import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import Grid from '@mui/material/Grid';
import {
  RowBackground,
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import * as css from './bestillingsliste.psa.module.scss';
import translations from './bestillingsliste.psa.i18n';
import {
  BrukerrollerDto,
  PraksisavtaleStedDto,
  PraksisavtaleStedDtoKapasitetstypeDtoEnum,
  PraksisavtalestedStatusDto,
} from '../api/generated/praksissted-generated-api';
import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { PsaUserContext } from '../user/PsaUserStore';
import Button, { ArrowToggleButton } from '../components/button';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import Icon, { IconType } from '../components/icon';
import { ConfirmationDialog } from '../components/dialog';
import { NotificationFunctionContext } from '../components/notifications/notificationProvider';
import { NumberInput } from '../components/input';
import useUnsavedChangesPrompt from '../hooks/useUnsavedChangesPrompt';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import { formatLocaleNo } from '../date.utils';
import { useActivityContextPSA } from './activitycontexts';
import OpenInNewTab from '../components/link/openInNewTab';
import { flatten, sum } from '../util/listutils';
import useAddErrorNotificationCallback from '../hooks/useAddErrorNotificationCallback';
import { isNullOrUndefined } from '../util/isNullOrUndefined';
import { useFormatMessageFor } from '../i18n/translation';
import useTableSearch from '../components/table/useTableSearch';
import { groupBy } from '../util';

type BestillingIdPraksisstedId = `${string}|${string}`;

type NearestBestillingIdPraksisstedIdToParentId = {
  [bestillingIdPraksisstedId: BestillingIdPraksisstedId]: string;
};

type BestillingIdPraksisstedIdBoolean = {
  [bestillingIdPraksisstedId: BestillingIdPraksisstedId]: boolean;
};

const getExpandedStateFromSessionStorage = (
  activityId: string,
): BestillingIdPraksisstedIdBoolean | undefined =>
  JSON.parse(
    // TODO Remove v2 after a couple of months (until sessions are all reset)
    sessionStorage.getItem(`expandedStederPSAInActivity-v2-${activityId}`) ??
      'null',
  ) || undefined;

const putExpandedStateInSessionStorage = (
  activityId: string,
  expanded: BestillingIdPraksisstedIdBoolean,
) =>
  sessionStorage.setItem(
    // TODO Remove v2 after a couple of months (until sessions are all reset)
    `expandedStederPSAInActivity-v2-${activityId}`,
    JSON.stringify(expanded),
  );

export interface PraksisstedWithHierarchyIndent extends PraksisavtaleStedDto {
  indent: number;
}

type PraksisstedIdsToKapasitet = { [praksisstedId: string]: number };

type BestillingIdsToBesvarelser = {
  [bestillingId: string]: PraksisstedIdsToKapasitet;
};

const getPraksisstederWithHierarchyIndent = (
  praksissteder: PraksisavtaleStedDto[],
  indent: number = 0,
): PraksisstedWithHierarchyIndent[] =>
  praksissteder.flatMap((toppnode) => {
    const praksisstedWithIndent = {
      ...toppnode,
      indent,
    };

    return [
      praksisstedWithIndent,
      ...getPraksisstederWithHierarchyIndent(
        praksisstedWithIndent.undernoder,
        praksisstedWithIndent.indent + 1,
      ),
    ];
  });

const flattenWithUndernoder = (
  praksissteder: PraksisavtaleStedDto[],
): PraksisavtaleStedDto[] =>
  flatten(praksissteder, (praksissted) => praksissted.undernoder);

const getAllPraksisstederAreBekreftetOrHasAllUndernoderBekreftet = (
  praksissteder: PraksisavtaleStedDto[],
): boolean =>
  praksissteder.every(
    (praksissted) =>
      (praksissted.status === PraksisavtalestedStatusDto.Bekreftet ||
        praksissted.undernoder.length > 0) &&
      getAllPraksisstederAreBekreftetOrHasAllUndernoderBekreftet(
        praksissted.undernoder,
      ),
  );

const getPraksisstedHasAllUndernoderBekreftet = (
  praksissted: PraksisavtaleStedDto,
): boolean =>
  praksissted.undernoder.length > 0 &&
  getAllPraksisstederAreBekreftetOrHasAllUndernoderBekreftet(
    praksissted.undernoder,
  );

const getBestillingIdsPraksisstedIdsHavingAllUndernoderBekreftet = (
  praksissteder: PraksisavtaleStedDto[],
): BestillingIdPraksisstedId[] =>
  flattenWithUndernoder(praksissteder).reduce(
    (acc: BestillingIdPraksisstedId[], praksissted: PraksisavtaleStedDto) =>
      getPraksisstedHasAllUndernoderBekreftet(praksissted)
        ? acc.concat(getBestillingIdPraksisstedId(praksissted))
        : acc,
    [],
  );

const MIN_SEARCH_LENGTH = 2;

const isKapasitetZeroOnAnyBestilling = (
  besvarelser: BestillingIdsToBesvarelser,
  praksissteder: PraksisavtaleStedDto[],
) => {
  const bestillingIds = praksissteder.map(
    (praksissted) => praksissted.bestillingId,
  );

  return (
    bestillingIds.some((id) => !besvarelser.hasOwnProperty(id)) ||
    Object.values(besvarelser).some(
      (besvarelse) => sum(Object.values(besvarelse)) === 0,
    )
  );
};

const flattenPraksisstederForBesvarelser = (
  praksissteder: PraksisavtaleStedDto[],
): BestillingIdsToBesvarelser => {
  const praksisstederWithRegisteredKapasitet = flattenWithUndernoder(
    praksissteder,
  ).filter((praksissted) => !isNullOrUndefined(praksissted.kapasitet));

  return groupByBestillingIdEntries(
    praksisstederWithRegisteredKapasitet,
  ).reduce((acc: BestillingIdsToBesvarelser, [bestillingId, praksissteder]) => {
    acc[bestillingId] = praksissteder.reduce(
      (
        praksisstedIdsToKapasitet: PraksisstedIdsToKapasitet,
        { id: praksisstedId, kapasitet },
      ) => {
        praksisstedIdsToKapasitet[praksisstedId] = kapasitet || 0;

        return praksisstedIdsToKapasitet;
      },
      {},
    );

    return acc;
  }, {});
};

const getInitExpandedAvtaleHierarki = (
  steder: PraksisavtaleStedDto[],
  indent: number = 0,
): BestillingIdPraksisstedIdBoolean =>
  steder.reduce(
    (acc: BestillingIdPraksisstedIdBoolean, sted: PraksisavtaleStedDto) => {
      acc[`${sted.bestillingId}|${sted.id}`] = indent < 1;

      return {
        ...acc,
        ...getInitExpandedAvtaleHierarki(sted.undernoder, indent + 1),
      };
    },
    {},
  );

const collapseAllUndernoder = (
  praksissteder: PraksisavtaleStedDto[],
): BestillingIdPraksisstedIdBoolean =>
  praksissteder.reduce(
    (acc, praksissted) => ({
      ...acc,
      [`${praksissted.bestillingId}|${praksissted.id}`]: false,
      ...collapseAllUndernoder(praksissted.undernoder),
    }),
    {},
  );

// Exported for tests
export const getBestillingIdPraksisstedId = (
  praksissted: PraksisavtaleStedDto,
): BestillingIdPraksisstedId => `${praksissted.bestillingId}|${praksissted.id}`;

const mapAvtalestedToNearestParent = (
  praksissteder: PraksisavtaleStedDto[],
  parentId: string,
): NearestBestillingIdPraksisstedIdToParentId =>
  praksissteder?.reduce(
    (
      acc: NearestBestillingIdPraksisstedIdToParentId,
      sted: PraksisavtaleStedDto,
    ) => ({
      ...acc,
      [getBestillingIdPraksisstedId(sted)]: parentId,
      ...mapAvtalestedToNearestParent(sted.undernoder, sted.id),
    }),
    {},
  );

const getPraksisstedHasMissingConfirmation = (
  praksissted: PraksisavtaleStedDto,
): string[] => {
  const selfIsUbekreftet =
    praksissted.status === PraksisavtalestedStatusDto.Ubekreftet &&
    praksissted.kanEndre;

  const ubekreftetUndernodeIds = praksissted.undernoder.flatMap((undernode) =>
    getPraksisstedHasMissingConfirmation(undernode),
  );

  return selfIsUbekreftet
    ? [praksissted.id, ...ubekreftetUndernodeIds]
    : ubekreftetUndernodeIds;
};

const groupByBestillingId = (
  praksissteder: PraksisavtaleStedDto[],
): { [bestillingId: string]: PraksisavtaleStedDto[] } =>
  groupBy(praksissteder, (praksissted) => praksissted.bestillingId);

const groupByBestillingIdEntries = (
  praksissteder: PraksisavtaleStedDto[],
): [string, PraksisavtaleStedDto[]][] =>
  Object.entries(groupByBestillingId(praksissteder));

const getBestillingIdsToPraksisstedIdsWithMissingConfirmation = (
  praksisstederToCheck: PraksisavtaleStedDto[],
): { [bestillingId: string]: string[] } =>
  groupByBestillingIdEntries(praksisstederToCheck).reduce(
    (
      acc: { [bestillingId: string]: string[] },
      [bestillingId, praksissteder],
    ) => ({
      ...acc,
      [bestillingId]: praksissteder.flatMap((praksissted) =>
        getPraksisstedHasMissingConfirmation(praksissted),
      ),
    }),
    {},
  );

const mapBesvarelserToDto = (
  bestillingerToBesvarelser: BestillingIdsToBesvarelser,
) =>
  Object.entries(bestillingerToBesvarelser).flatMap(
    ([bestillingId, praksisstederMedKapasitet]) =>
      Object.entries(praksisstederMedKapasitet).map(
        ([praksisstedId, kapasitet]) => ({
          bestillingId,
          kapasitet,
          praksisstedId,
        }),
      ),
  );

const hasAvtalefeil = (
  bestillingId: string,
  praksisstedId: string,
  bestillingIdsToPraksissteder: {
    [bestillingId: string]: PraksisavtaleStedDto[];
  },
): boolean =>
  bestillingIdsToPraksissteder.hasOwnProperty(bestillingId) &&
  bestillingIdsToPraksissteder[bestillingId].find(
    (praksissted) => praksissted.id === praksisstedId,
  )?.status === PraksisavtalestedStatusDto.Avtalefeil;

enum Confirmation {
  ZeroCapacity,
  Bindende,
  BekreftAgain,
}

export enum TableHeaderType {
  AVDELING = 'Avdeling',
  AVTALE = 'Avtale',
  STATUS = 'Status',
  BESTILLING = 'Bestilling',
  KAPASITET = 'Kapasitet',
}

export type TableHeaderTypesToTranslations = {
  [headerType in TableHeaderType]: string;
};

interface PraksisstederFromPraksisaktivitetState {
  initBesvarelser: BestillingIdsToBesvarelser;
  besvarelser: BestillingIdsToBesvarelser;
  setBesvarelser: Dispatch<SetStateAction<BestillingIdsToBesvarelser>>;
  alleBestillingerWithoutAvtalefeilBekreftet: boolean;
  ingenEndring: boolean;
  bestillingPraksisstedIdsHavingAllUndernoderBekreftet: BestillingIdPraksisstedId[];
}

const usePraksisstederFromPraksisaktivitetBestillingerPage = (
  praksissteder: PraksisavtaleStedDto[],
): PraksisstederFromPraksisaktivitetState => {
  const {
    initBesvarelser,
    initBestillingIdsToPraksissteder,
    bestillingPraksisstedIdsHavingAllUndernoderBekreftet,
    alleBestillingerWithoutAvtalefeilBekreftet,
  } = useMemo(() => {
    const initBesvarelser = flattenPraksisstederForBesvarelser(praksissteder);
    const initBestillingIdsToPraksissteder = groupByBestillingId(
      flattenWithUndernoder(praksissteder),
    );

    const bestillingPraksisstedIdsHavingAllUndernoderBekreftet =
      getBestillingIdsPraksisstedIdsHavingAllUndernoderBekreftet(praksissteder);

    const missingConfirmations =
      getBestillingIdsToPraksisstedIdsWithMissingConfirmation(praksissteder);

    const alleBestillingerWithoutAvtalefeilBekreftet = !Object.entries(
      initBestillingIdsToPraksissteder,
    ).some(([bestillingId, praksissteder]) =>
      missingConfirmations[bestillingId].some(
        (praksisstedId) =>
          praksissteder
            .map((praksissted) => praksissted.id)
            .includes(praksisstedId) &&
          !hasAvtalefeil(
            bestillingId,
            praksisstedId,
            initBestillingIdsToPraksissteder,
          ),
      ),
    );

    return {
      initBesvarelser,
      initBestillingIdsToPraksissteder,
      bestillingPraksisstedIdsHavingAllUndernoderBekreftet,
      alleBestillingerWithoutAvtalefeilBekreftet,
    };
  }, [praksissteder]);

  const [besvarelser, setBesvarelser] =
    useState<BestillingIdsToBesvarelser>(initBesvarelser);

  const ingenEndring = useMemo(
    () =>
      Object.entries(besvarelser).every(
        ([bestillingId, praksissteder]) =>
          initBesvarelser.hasOwnProperty(bestillingId) &&
          Object.entries(praksissteder)
            .filter(
              ([praksisstedId]) =>
                !hasAvtalefeil(
                  bestillingId,
                  praksisstedId,
                  initBestillingIdsToPraksissteder,
                ),
            )
            .every(([praksisstedId, kapasitet]) => {
              const initKapasitet =
                initBesvarelser[bestillingId][praksisstedId];
              const zeroOrNullishInitKapasitet =
                isNullOrUndefined(initKapasitet) || initKapasitet === 0;
              const zeroOrNullishKapasitet =
                isNullOrUndefined(kapasitet) || kapasitet === 0;

              return (
                (zeroOrNullishInitKapasitet && zeroOrNullishKapasitet) ||
                initKapasitet === kapasitet
              );
            }),
      ),
    [initBesvarelser, besvarelser, initBestillingIdsToPraksissteder],
  );

  return {
    initBesvarelser,
    besvarelser,
    bestillingPraksisstedIdsHavingAllUndernoderBekreftet:
      bestillingPraksisstedIdsHavingAllUndernoderBekreftet,
    alleBestillingerWithoutAvtalefeilBekreftet,
    ingenEndring,
    setBesvarelser,
  };
};

const BestillingerPage = () => {
  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const errorNotificationCallback = useAddErrorNotificationCallback();
  const { praksiskalenderApi } = useContext(PraksisstedApiContext);
  const { praksisaktivitet, reloadPraksisaktivitet } = useActivityContextPSA();
  const { praksissteder } = praksisaktivitet;

  const labels = useFormatMessageFor(translations);

  const {
    initBesvarelser,
    besvarelser,
    setBesvarelser,
    alleBestillingerWithoutAvtalefeilBekreftet,
    ingenEndring,
    bestillingPraksisstedIdsHavingAllUndernoderBekreftet,
  } = usePraksisstederFromPraksisaktivitetBestillingerPage(praksissteder);

  const onBesvarelseChange = (
    bestillingId: string,
    praksisstedId: string,
    kapasitet: number,
  ) => {
    if (!initBesvarelser.hasOwnProperty(bestillingId) && !kapasitet) {
      setBesvarelser(
        Object.keys(besvarelser).reduce(
          (acc: BestillingIdsToBesvarelser, id: string) => {
            if (bestillingId !== id) {
              acc[id] = besvarelser[id];
            }

            return acc;
          },
          {},
        ),
      );
    } else {
      const besvarelserOnBestilling = besvarelser[bestillingId] || {};

      const updatedBesvarelserOnBestilling = {
        ...besvarelserOnBestilling,
        [praksisstedId]: kapasitet,
      };

      setBesvarelser({
        ...besvarelser,
        [bestillingId]: updatedBesvarelserOnBestilling,
      });
    }
  };

  const saveHandler = (bekreftet: boolean) => {
    const { id } = praksisaktivitet;

    const toppnodeEndenodeBesvarelser = bekreftet
      ? praksissteder
          .filter((praksissted) => praksissted.undernoder.length === 0)
          .reduce((acc: BestillingIdsToBesvarelser, praksissted) => {
            const { bestillingId } = praksissted;
            const { id: praksisstedId } = praksissted;
            const prevBesvarelserOnBestillingAcc = acc[bestillingId] || {};
            const besvarelserOnBestilling = besvarelser[bestillingId] || {};

            // Set capacity to zero on all top leaf nodes, when undefined - needed to make the bestilling bekreftet
            const kapasitet = besvarelserOnBestilling[praksisstedId] || 0;

            acc[bestillingId] = {
              ...prevBesvarelserOnBestillingAcc,
              ...besvarelserOnBestilling,
              [praksisstedId]: kapasitet,
            };

            return acc;
          }, {})
      : {};

    const newBesvarelser = mapBesvarelserToDto({
      ...besvarelser,
      ...toppnodeEndenodeBesvarelser,
    });

    praksiskalenderApi
      .praksisaktivitetIdPut({
        pathParams: { id },
        praksisaktivitetPutDto: {
          bekreftet,
          besvarelser: newBesvarelser,
        },
      })
      .then(() => {
        addSuccessNotification(labels.lagretMsg);
        reloadPraksisaktivitet();
      })
      .catch(errorNotificationCallback);
  };

  const [pendingConfirmations, setPendingConfirmations] = useState<
    Confirmation[]
  >([]);

  const confirmationCloseHandler = (okClicked: boolean) => {
    if (!okClicked) {
      setPendingConfirmations([]);
      return;
    }

    // OK was clicked - show next confirmation popup or save:
    const confirmations = [...pendingConfirmations];
    confirmations.shift();

    if (confirmations.length === 0) {
      setPendingConfirmations([]);
      saveHandler(true);
      return;
    }

    // Show next popup:
    setPendingConfirmations(confirmations);
  };

  const onBekreft = () => {
    const confirmations = [];

    if (isKapasitetZeroOnAnyBestilling(besvarelser, praksissteder)) {
      confirmations.push(Confirmation.ZeroCapacity);
    }

    confirmations.push(Confirmation.Bindende);

    if (alleBestillingerWithoutAvtalefeilBekreftet) {
      confirmations.push(Confirmation.BekreftAgain);
    }

    setPendingConfirmations(confirmations);
  };

  return (
    <>
      <Grid container>
        <PraksisstederTable
          onBesvarelseChange={onBesvarelseChange}
          bestillingPraksisstedIdsHavingAllUndernoderBekreftet={
            bestillingPraksisstedIdsHavingAllUndernoderBekreftet
          }
        />
        <Handlingsknapper
          bestillingBekreftet={alleBestillingerWithoutAvtalefeilBekreftet}
          ingenEndring={ingenEndring}
          onSave={() => {
            saveHandler(false);
          }}
          onBekreft={onBekreft}
        />
      </Grid>
      {pendingConfirmations.length > 0 && (
        <PendingConfirmations
          confirmationCloseHandler={confirmationCloseHandler}
          confirmation={pendingConfirmations[0]}
        />
      )}
    </>
  );
};

interface ConfirmationContentProps {
  confirmation: Confirmation;
}

const ConfirmationContent = ({ confirmation }: ConfirmationContentProps) => {
  const {
    praksisaktivitet: { sted },
  } = useActivityContextPSA();

  const institusjonsnavn = sted.navn[0];
  const labels = useFormatMessageFor(translations);

  switch (confirmation) {
    case Confirmation.BekreftAgain:
      return <p>{labels.bekreftetconfirmContents}</p>;
    case Confirmation.Bindende:
      return (
        <>
          <p>
            {labels.kapasitetBindendeConfirmContents1}
            <b>{institusjonsnavn}</b>.
          </p>
          <p>{labels.kapasitetBindendeConfirmContents2}</p>
        </>
      );
    case Confirmation.ZeroCapacity:
      return <p>{labels.kapasitetZeroConfirmContents}</p>;
  }
};

interface PendingConfirmationsProps {
  confirmationCloseHandler: (
    confirmed: boolean,
    dontShowAgain?: boolean,
  ) => void;
  confirmation: Confirmation;
}

const PendingConfirmations = ({
  confirmationCloseHandler,
  confirmation,
}: PendingConfirmationsProps) => {
  const {
    praksisaktivitet: { startDato, sluttDato },
  } = useActivityContextPSA();

  const labels = useFormatMessageFor({
    ...translations,
    kapasitetconfirmSubTitle: {
      ...translations.kapasitetconfirmSubTitle,
      values: {
        fromDate: formatLocaleNo(startDato),
        toDate: formatLocaleNo(sluttDato),
      },
    },
  });

  const getTitle = (confirmation: Confirmation): string => {
    switch (confirmation) {
      case Confirmation.BekreftAgain:
        return labels.bekreftetconfirmTitle;
      case Confirmation.Bindende:
      case Confirmation.ZeroCapacity:
        return labels.kapasitetconfirmTitle;
    }
  };

  const getSubtitle = (confirmation: Confirmation): string | undefined => {
    switch (confirmation) {
      case Confirmation.Bindende:
        return labels.kapasitetconfirmSubTitle;
      case Confirmation.BekreftAgain:
      case Confirmation.ZeroCapacity:
        return undefined;
    }
  };

  return (
    <ConfirmationDialog
      onClose={confirmationCloseHandler}
      title={getTitle(confirmation)}
      subtitle={getSubtitle(confirmation)}
      isOpen
    >
      <ConfirmationContent confirmation={confirmation} />
    </ConfirmationDialog>
  );
};

interface PraksisstederFromPraksisaktivitetBestillingerTableState {
  praksisstedIdsToNearestParent: NearestBestillingIdPraksisstedIdToParentId;
  praksisstederWithHierarchyIndent: PraksisstedWithHierarchyIndent[];
}

const usePraksisstederFromPraksisaktivitetBestillingerTable = (
  praksissteder: PraksisavtaleStedDto[],
): PraksisstederFromPraksisaktivitetBestillingerTableState =>
  useMemo(() => {
    const praksisstedIdsToNearestParent = praksissteder.reduce(
      (
        acc: NearestBestillingIdPraksisstedIdToParentId,
        praksissted: PraksisavtaleStedDto,
      ) => ({
        ...acc,
        ...mapAvtalestedToNearestParent(praksissted.undernoder, praksissted.id),
      }),
      {},
    );

    const praksisstederWithHierarchyIndent =
      getPraksisstederWithHierarchyIndent(praksissteder);

    return { praksisstedIdsToNearestParent, praksisstederWithHierarchyIndent };
  }, [praksissteder]);

interface PraksisstederTableProps {
  onBesvarelseChange: (
    bestillingId: string,
    praksisstedId: string,
    kapasitet: number,
  ) => void;
  bestillingPraksisstedIdsHavingAllUndernoderBekreftet: BestillingIdPraksisstedId[];
}

const PraksisstederTable = ({
  onBesvarelseChange,
  bestillingPraksisstedIdsHavingAllUndernoderBekreftet,
}: PraksisstederTableProps) => {
  const {
    praksisaktivitet: { praksissteder, id: praksisaktivitetId },
  } = useActivityContextPSA();

  const { praksisstedIdsToNearestParent, praksisstederWithHierarchyIndent } =
    usePraksisstederFromPraksisaktivitetBestillingerTable(praksissteder);

  const labels = useFormatMessageFor(translations);

  const [expandedPraksisstedRows, setExpandedPraksisstedRows] =
    useState<BestillingIdPraksisstedIdBoolean>(
      getExpandedStateFromSessionStorage(praksisaktivitetId) ??
        getInitExpandedAvtaleHierarki(praksissteder),
    );

  const setExpandedAndUpdateSessionStorage = (
    expand: BestillingIdPraksisstedIdBoolean,
  ) => {
    putExpandedStateInSessionStorage(praksisaktivitetId, expand);
    setExpandedPraksisstedRows(expand);
  };

  const toggleExpanded = (praksissted: PraksisavtaleStedDto) => {
    const { id, undernoder, bestillingId } = praksissted;
    const shouldExpand = !expandedPraksisstedRows[`${bestillingId}|${id}`];

    const expandUpdate = {
      ...expandedPraksisstedRows,
      [`${bestillingId}|${id}`]: shouldExpand,
    };

    setExpandedAndUpdateSessionStorage({
      ...expandUpdate,
      ...(shouldExpand ? {} : collapseAllUndernoder(undernoder)),
    });
  };

  const tableHeaderTypesWithTranslations: TableHeaderTypesToTranslations = {
    [TableHeaderType.AVDELING]: labels.avdeling,
    [TableHeaderType.AVTALE]: labels.avtale,
    [TableHeaderType.STATUS]: labels.status,
    [TableHeaderType.BESTILLING]: labels.bestilling,
    [TableHeaderType.KAPASITET]: labels.kapasitet,
  };

  const [activeCol, setActiveCol] = useState<number>(-1);
  const [tableSearch, setTableSearch] = useState<string>('');

  const headers = Object.entries(tableHeaderTypesWithTranslations).map(
    // TODO Make sort/search based on type and not index, when default sort/activeCol etc. is added
    ([type, title], index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        search={(col, value) => setTableSearch(value)}
        canSearch={type === TableHeaderType.AVDELING} // Only column praksissteder can search
      >
        {title}
      </TableCellTh>
    ),
  );

  const shouldShow = (praksissted: PraksisstedWithHierarchyIndent) => {
    const { id, bestillingId } = praksissted;

    const parentId = praksisstedIdsToNearestParent[`${bestillingId}|${id}`];

    return !parentId || expandedPraksisstedRows[`${bestillingId}|${parentId}`];
  };

  const filteredPraksissteder = useTableSearch(
    praksisstederWithHierarchyIndent,
    [[tableSearch, (praksissted) => praksissted.navn]],
    MIN_SEARCH_LENGTH,
  );

  const rows = filteredPraksissteder
    .filter((praksissted) => shouldShow(praksissted))
    .map((praksissted) => {
      const bestillingIdPraksisstedId =
        getBestillingIdPraksisstedId(praksissted);
      const togglePraksisstedExpanded = () => toggleExpanded(praksissted);
      const bekreftetByUndernoder =
        bestillingPraksisstedIdsHavingAllUndernoderBekreftet.includes(
          bestillingIdPraksisstedId,
        );

      return (
        <BestillingTableRow
          key={praksissted.id + praksissted.bestillingId}
          praksissted={praksissted}
          isExpanded={
            expandedPraksisstedRows[
              `${praksissted.bestillingId}|${praksissted.id}`
            ]
          }
          toggleExpanded={togglePraksisstedExpanded}
          onBesvarelseChange={onBesvarelseChange}
          headers={tableHeaderTypesWithTranslations}
          isExpandedByParent={
            tableSearch.length === 0 &&
            !expandedPraksisstedRows[
              `${praksissted.bestillingId}|${praksissted.id}`
            ]
          }
          bekreftetByUndernoder={bekreftetByUndernoder}
        />
      );
    });

  return (
    <Grid xs={12} item>
      <div className={css.table}>
        <Table>
          <TableHeader>
            <TableRow top>{headers}</TableRow>
          </TableHeader>
          <TableMain>{rows}</TableMain>
        </Table>
      </div>
    </Grid>
  );
};

interface HandlingsknapperWrapperProps {
  children?: ReactNode;
}

const HandlingsknapperWrapper = ({
  children,
}: HandlingsknapperWrapperProps) => (
  <Grid xs={12} item>
    <div className={css.buttonContainer}>{children}</div>
  </Grid>
);

interface HandlingsknapperProps {
  bestillingBekreftet: boolean;
  ingenEndring: boolean;
  onSave: () => void;
  onBekreft: () => void;
}

const Handlingsknapper = ({
  bestillingBekreftet,
  ingenEndring,
  onSave,
  onBekreft,
}: HandlingsknapperProps) => {
  const { praksisaktivitet } = useActivityContextPSA();
  const { publisertTilFs } = praksisaktivitet;
  const { psaUser: bruker } = useContext(PsaUserContext);
  const canWrite =
    bruker && bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet;

  const labels = useFormatMessageFor(translations);

  useUnsavedChangesPrompt(!ingenEndring);

  if (!canWrite) return <HandlingsknapperWrapper />;

  return (
    <HandlingsknapperWrapper>
      {!bestillingBekreftet && (
        <Button.Secondary
          id={'lagreBtn'}
          disabled={ingenEndring}
          onClick={onSave}
        >
          {labels.lagre}
        </Button.Secondary>
      )}
      <Button.Secondary
        id={'bekreftBtn'}
        disabled={(bestillingBekreftet && ingenEndring) || publisertTilFs}
        onClick={onBekreft}
      >
        {labels.bekreft}
      </Button.Secondary>
    </HandlingsknapperWrapper>
  );
};

const Spacer: React.FC<
  React.PropsWithChildren<{
    width: number;
  }>
> = ({ width }) => (
  <span
    style={{
      display: 'inline-block',
      height: '25px',
      width: width * 24 + 'px',
    }}
  />
);

interface AvtaleCellProps {
  avtaleId: string;
  avtalenavn: string;
  headers: TableHeaderTypesToTranslations;
}

const AvtaleCell = ({ headers, avtaleId, avtalenavn }: AvtaleCellProps) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      showAvtale: formatMessage({ id: translations.showAvtale.id }),
    }),
    [formatMessage],
  );

  return (
    <TableCellTd header={headers[TableHeaderType.AVTALE]}>
      <OpenInNewTab
        to={`/myorganization/agreements/${avtaleId}`}
        label={labels.showAvtale}
        title={avtalenavn}
      />
    </TableCellTd>
  );
};

interface BestillingTableRowIconProps {
  id: string;
}

const BestillingTableRowIcon = ({ id }: BestillingTableRowIconProps) => (
  <div className={css.icon}>
    <Icon type={IconType.EndNode} htmlId={`${id}-end`} />
  </div>
);

interface BestillingTableRowIconToggleExpandedBtnProps {
  praksissted: PraksisstedWithHierarchyIndent;
  toggleExpanded: () => void;
  isExpandedNotByParent: boolean;
}

const BestillingTableRowIconToggleExpandedBtn = ({
  praksissted,
  toggleExpanded,
  isExpandedNotByParent,
}: BestillingTableRowIconToggleExpandedBtnProps) => {
  const { undernoder, id } = praksissted;

  if (undernoder.length === 0) return <BestillingTableRowIcon id={id} />;

  return (
    <ArrowToggleButton
      iconType={
        isExpandedNotByParent
          ? IconType.CloseRowTriangle
          : IconType.OpenRowTriangle
      }
      onClick={toggleExpanded}
      className={css.arrowToggleBtn}
    />
  );
};

interface BestillingTableRowProps {
  praksissted: PraksisstedWithHierarchyIndent;
  isExpanded: boolean;
  isExpandedByParent: boolean;
  toggleExpanded: () => void;
  onBesvarelseChange: (
    bestillingId: string,
    praksisstedId: string,
    kapasitet: number,
  ) => void;
  headers: TableHeaderTypesToTranslations;
  bekreftetByUndernoder: boolean;
}

const useStatusMessage = (
  { status, kapasitetstype, kapasitet }: PraksisstedWithHierarchyIndent,
  bekreftetByUndernoder: boolean,
): string | (string | React.JSX.Element)[] | React.JSX.Element => {
  const { formatMessage } = useIntl();

  // Does not use useFormatMessageFor, as kapasitet is an injected element
  const studiestedKapasitet = formatMessage(
    { id: translations.studiestedKapasitet.id },
    { kapasitet: <b>{kapasitet}</b> },
  );

  const labels = useFormatMessageFor(translations);

  if (status === PraksisavtalestedStatusDto.Avtalefeil) {
    return labels.avtalefeil;
  }

  if (kapasitetstype === PraksisavtaleStedDtoKapasitetstypeDtoEnum.Studiested) {
    return studiestedKapasitet;
  }

  switch (status) {
    case PraksisavtalestedStatusDto.Bekreftet:
      return labels.bekreftet;
    case PraksisavtalestedStatusDto.Ubekreftet:
      return labels.ubekreftet;
    case PraksisavtalestedStatusDto.UtenBesvarelse:
      return bekreftetByUndernoder
        ? labels.bekreftetUnder
        : labels.ubekreftetUnder;
  }
};

export const BestillingTableRow = ({
  praksissted,
  isExpanded,
  toggleExpanded,
  onBesvarelseChange,
  headers,
  isExpandedByParent,
  bekreftetByUndernoder,
}: BestillingTableRowProps) => {
  const { avtalenavn, avtaleId, id, navn, indent, bestilling, status } =
    praksissted;

  const statusMessage = useStatusMessage(praksissted, bekreftetByUndernoder);
  const bestillingIdPraksisstedId = getBestillingIdPraksisstedId(praksissted);
  const isErroneous = status === PraksisavtalestedStatusDto.Avtalefeil;
  const rowBackground = isErroneous
    ? RowBackground.alert
    : RowBackground.default;

  const onKapasitetChange = (updatedKapasitet: number | undefined) => {
    onBesvarelseChange(
      praksissted.bestillingId,
      praksissted.id,
      updatedKapasitet ?? 0,
    );
  };

  return (
    <TableRow
      data-cy={`bestillingId-praksisstedId-${bestillingIdPraksisstedId}`}
      rowBackground={rowBackground}
    >
      <TableCellTd header={headers[TableHeaderType.AVDELING]}>
        <div className={css.practiceplace}>
          <div className={css.icon}>
            <Spacer width={indent} />
          </div>
          <BestillingTableRowIconToggleExpandedBtn
            praksissted={praksissted}
            toggleExpanded={toggleExpanded}
            isExpandedNotByParent={isExpanded && !isExpandedByParent}
          />
          <div className={indent === 0 ? css.nameStrong : css.name}>
            <NavLink to={`/myorganization/practiceplaces/profile/${id}`}>
              {navn}
            </NavLink>
          </div>
          <OnStedHoverCard sted={StedType.PRAKSISSTED} id={id} navn={navn} />
        </div>
      </TableCellTd>
      <AvtaleCell
        headers={headers}
        avtaleId={avtaleId}
        avtalenavn={avtalenavn}
      />
      <TableCellTd
        className={'status-cell'}
        header={headers[TableHeaderType.STATUS]}
      >
        {statusMessage}
      </TableCellTd>
      <TableCellTd header={headers[TableHeaderType.BESTILLING]}>
        {bestilling || undefined}
      </TableCellTd>
      <TableCellTd header={headers[TableHeaderType.KAPASITET]}>
        <Kapasitet
          onKapasitetChange={onKapasitetChange}
          praksissted={praksissted}
        />
      </TableCellTd>
    </TableRow>
  );
};

type KapasitetAndType = {
  kapasitet: number | undefined;
  kapasitetstype: PraksisavtaleStedDtoKapasitetstypeDtoEnum | undefined;
};

type KapasitetProps = {
  onKapasitetChange: (kapasitet: number | undefined) => void;
  praksissted: PraksisavtaleStedDto;
};

const getSumOfCapacities = (praksissted: PraksisavtaleStedDto): number =>
  praksissted.undernoder.reduce(
    (acc, undernode) => acc + getSumOfCapacities(undernode),
    praksissted.kapasitet || 0,
  );

const FormattedCapacitySum = (praksissted: PraksisavtaleStedDto) => {
  const sumOfCapacities = getSumOfCapacities(praksissted);

  return praksissted.undernoder.length > 0 ? (
    <span title={'Sum'}>{`Σ ${sumOfCapacities}`}</span>
  ) : (
    <>{sumOfCapacities}</>
  );
};

const Kapasitet = ({ onKapasitetChange, praksissted }: KapasitetProps) => {
  const { praksisaktivitet } = useActivityContextPSA();
  const [{ kapasitet, kapasitetstype }, setKapasitetAndType] =
    useState<KapasitetAndType>({
      kapasitet: praksissted.kapasitet,
      kapasitetstype:
        praksissted.kapasitetstype ||
        PraksisavtaleStedDtoKapasitetstypeDtoEnum.Praksissted,
    });

  const { psaUser: bruker } = useContext(PsaUserContext);
  const disabled =
    (bruker && bruker.hoeyesteRettighet === BrukerrollerDto.Leserettighet) ||
    praksisaktivitet.publisertTilFs ||
    praksissted.status === PraksisavtalestedStatusDto.Avtalefeil;

  const onChange = (updatedKapasitet: number | undefined) => {
    setKapasitetAndType({
      kapasitet: updatedKapasitet,
      kapasitetstype: PraksisavtaleStedDtoKapasitetstypeDtoEnum.Praksissted,
    });

    onKapasitetChange(updatedKapasitet);
  };

  return praksissted.kanEndre ? (
    <NumberInput
      containerClassName={css.boxSizing}
      disabled={disabled}
      ariaLabel={'places ordered'}
      htmlId={'placesOrdered'}
      value={kapasitet}
      onChange={onChange}
      highlighted={
        kapasitetstype === PraksisavtaleStedDtoKapasitetstypeDtoEnum.Studiested
      }
    />
  ) : (
    <FormattedCapacitySum {...praksissted} />
  );
};

export default BestillingerPage;
